import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { use2FA } from '../../hooks/use2FA';
import { Setup2FADialog } from './Setup2FADialog';

export const TwoFactorSettings: React.FC = () => {
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { status, isLoadingStatus, disable, isDisabling } = use2FA();

  const handleDisable = async () => {
    try {
      await disable(disableCode);
      setSuccessMessage('Two-factor authentication has been disabled');
      setDisableDialogOpen(false);
      setDisableCode('');
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to disable 2FA');
    }
  };

  const handleSetupSuccess = () => {
    setSuccessMessage('Two-factor authentication has been enabled successfully!');
    setSetupDialogOpen(false);
  };

  if (isLoadingStatus) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <SecurityIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Two-Factor Authentication</Typography>
            {status?.isEnabled && (
              <Chip
                label="Enabled"
                color="success"
                size="small"
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>

          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" paragraph>
            Add an extra layer of security to your account by requiring a verification code in
            addition to your password when signing in.
          </Typography>

          {status?.isEnabled ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                Enabled on: {new Date(status.enabledAt!).toLocaleDateString()}
              </Typography>
              {status.recoveryCodesRemaining !== undefined && (
                <Typography variant="body2" gutterBottom>
                  Recovery codes remaining: {status.recoveryCodesRemaining}
                </Typography>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDisableDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Disable 2FA
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={() => setSetupDialogOpen(true)}
            >
              Enable 2FA
            </Button>
          )}
        </CardContent>
      </Card>

      <Setup2FADialog
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSuccess={handleSetupSuccess}
      />

      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling 2FA will make your account less secure.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Enter a verification code from your authenticator app to confirm:
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            inputProps={{ maxLength: 6 }}
            sx={{ mt: 2 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDisable}
            variant="contained"
            color="error"
            disabled={disableCode.length !== 6 || isDisabling}
          >
            {isDisabling ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
